import PropTypes from "prop-types";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input, InputNumber, Select, SettingsCard, Space, Tooltip } from "top-component-library";
import { FormRow, LabelWithTooltip, SettingHint } from "../styles";

// Seated Cut Percentages, nested inside Basic Information in legacy - only shown for a
// customer referred in via Seated. Its own save action, separate from the rest of the card.
const SeatedCutFields = ({ seatedCut, onChange, onSave }) => (
  <SettingsCard title="Seated Cut Percentages">
    <SettingHint>Percent from menuitems (pre-tax). Used for Seated Rewards</SettingHint>
    <FormRow>
      <label htmlFor="seated-items-pretax">Items pre-tax %</label>
      <Input
        id="seated-items-pretax"
        type="number"
        min={0}
        max={100}
        step={0.001}
        value={seatedCut.seatedItemsPretax}
        onChange={(event) => onChange("seatedItemsPretax", Number(event.target.value))}
      />
    </FormRow>
    <SettingHint>Percent from menuitem sales tax. Should generally be 0.</SettingHint>
    <FormRow>
      <label htmlFor="seated-items-tax">Items tax %</label>
      <Input
        id="seated-items-tax"
        type="number"
        min={0}
        max={100}
        step={0.001}
        value={seatedCut.seatedItemsTax}
        onChange={(event) => onChange("seatedItemsTax", Number(event.target.value))}
      />
    </FormRow>
    <SettingHint>
      If you&apos;ll be configuring a specific service charge to go to Seated, do not use the &quot;all service
      charge&quot; percentages below; set them to 0.
    </SettingHint>
    <SettingHint>Percent from all service charges (pre-tax)</SettingHint>
    <FormRow>
      <label htmlFor="seated-service-charge-pretax">Service charge pre-tax %</label>
      <Input
        id="seated-service-charge-pretax"
        type="number"
        min={0}
        max={100}
        step={0.001}
        value={seatedCut.seatedServiceChargePretax}
        onChange={(event) => onChange("seatedServiceChargePretax", Number(event.target.value))}
      />
    </FormRow>
    <SettingHint>Percent from all service charges sales tax</SettingHint>
    <FormRow>
      <label htmlFor="seated-service-charge-tax">Service charge tax %</label>
      <Input
        id="seated-service-charge-tax"
        type="number"
        min={0}
        max={100}
        step={0.001}
        value={seatedCut.seatedServiceChargeTax}
        onChange={(event) => onChange("seatedServiceChargeTax", Number(event.target.value))}
      />
    </FormRow>
    <Checkbox
      checked={seatedCut.seatedMakeRetroactive}
      onChange={(event) => onChange("seatedMakeRetroactive", event.target.checked)}
    >
      Apply this cut retroactively, to past days&apos; sales (usually no)
    </Checkbox>
    <Button onClick={onSave}>Set Seated Cut</Button>
  </SettingsCard>
);

SeatedCutFields.propTypes = {
  seatedCut: PropTypes.shape({
    seatedItemsPretax: PropTypes.number.isRequired,
    seatedItemsTax: PropTypes.number.isRequired,
    seatedServiceChargePretax: PropTypes.number.isRequired,
    seatedServiceChargeTax: PropTypes.number.isRequired,
    seatedMakeRetroactive: PropTypes.bool.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

// Mirrors editCustomerProperties.html's "Basic Information" card. The Referral Partner
// select is deliberately not ported as an editable control: editOwnerProperties() (backend)
// rejects any payload where referrer differs from the stored value ("Cannot edit referrer
// from this menu"), so in legacy this select can display a different value but can never
// actually save one - changing it always errors, directing the admin to Switch Referrer
// instead (Danger Zone, a later stage). Shown here read-only rather than as a control that
// looks editable but silently can't be.
const BasicInformationCard = ({
  timezone,
  tzChoices,
  currency,
  currencies,
  locale,
  locales,
  salesforceId,
  isDashPay,
  ddStoreId,
  sevenroomsVenueId,
  seatedVenueId,
  referrer,
  seatedCut,
  minimumPaidOrderCents,
  allowFreeOrders,
  allowFreeOrdersAfterPurchase,
  onTimezoneChange,
  onCurrencyChange,
  onLocaleChange,
  onSalesforceIdChange,
  onIsDashPayChange,
  onDdStoreIdChange,
  onSevenroomsVenueIdChange,
  onSeatedVenueIdChange,
  onSeatedCutChange,
  onSaveSeatedCut,
  onMinimumPaidOrderCentsChange,
  onAllowFreeOrdersChange,
  onAllowFreeOrdersAfterPurchaseChange,
  onSave,
}) => (
  <SettingsCard title="Basic Information">
    <SettingHint>
      Timezone controls reports and open hours. Contact TOP by DoorDash engineering if the customer&apos;s timezone
      isn&apos;t listed; we can add it.
    </SettingHint>
    <FormRow>
      <label htmlFor="customer-properties-timezone">Timezone</label>
      <Select id="customer-properties-timezone" value={timezone} onChange={onTimezoneChange}>
        {tzChoices.map((choice) => (
          <Select.Option key={choice} value={choice}>
            {choice}
          </Select.Option>
        ))}
      </Select>
    </FormRow>
    <FormRow>
      <label htmlFor="customer-properties-currency">Currency</label>
      <Select id="customer-properties-currency" value={currency} onChange={onCurrencyChange}>
        {currencies.map((option) => (
          <Select.Option key={option.code} value={option.code}>
            {option.code}
          </Select.Option>
        ))}
      </Select>
    </FormRow>
    <FormRow>
      <label htmlFor="customer-properties-locale">Locale</label>
      <Select id="customer-properties-locale" value={locale} onChange={onLocaleChange}>
        {(locales[currency] ?? []).map((option) => (
          <Select.Option key={option} value={option}>
            {option}
          </Select.Option>
        ))}
      </Select>
    </FormRow>
    <FormRow>
      <label htmlFor="customer-properties-salesforce-id">Salesforce Account ID</label>
      <Input
        id="customer-properties-salesforce-id"
        placeholder="copy from Salesforce Account Detail page"
        value={salesforceId ?? ""}
        onChange={(event) => onSalesforceIdChange(event.target.value)}
      />
    </FormRow>
    <Checkbox checked={isDashPay} onChange={(event) => onIsDashPayChange(event.target.checked)}>
      <LabelWithTooltip>
        <span>Is Dash Pay Mx</span>
        <Tooltip title="Is this Bbot Mx used to enable DashPay via Verano.">
          <QuestionCircleOutlined />
        </Tooltip>
      </LabelWithTooltip>
    </Checkbox>
    <FormRow>
      <label htmlFor="customer-properties-dd-store-id">DoorDash Store ID</label>
      <Input
        id="customer-properties-dd-store-id"
        value={ddStoreId ?? ""}
        onChange={(event) => onDdStoreIdChange(event.target.value)}
      />
    </FormRow>
    <FormRow>
      <label htmlFor="customer-properties-sevenrooms-venue-id">SevenRooms Venue ID</label>
      <Input
        id="customer-properties-sevenrooms-venue-id"
        value={sevenroomsVenueId ?? ""}
        onChange={(event) => onSevenroomsVenueIdChange(event.target.value)}
      />
    </FormRow>
    <FormRow>
      <label htmlFor="customer-properties-seated-venue-id">Seated Venue ID</label>
      <Input
        id="customer-properties-seated-venue-id"
        value={seatedVenueId ?? ""}
        onChange={(event) => onSeatedVenueIdChange(event.target.value)}
      />
    </FormRow>
    <FormRow>
      <label>Referral Partner</label>
      <div>{referrer || "No referral"}</div>
      <SettingHint>
        <i>Tip: Use Switch Referrer in the Danger Zone below to change this.</i>
      </SettingHint>
    </FormRow>
    {referrer === "seated" && seatedCut && (
      <SeatedCutFields seatedCut={seatedCut} onChange={onSeatedCutChange} onSave={onSaveSeatedCut} />
    )}
    <FormRow>
      <label htmlFor="customer-properties-minimum-paid-order">
        <LabelWithTooltip>
          <span>Minimum cart for paid orders ($)</span>
          <Tooltip
            title={
              "Rejects orders that are non-free but where the total of cart items (before tax, tip, discounts, " +
              'and delivery charge) is below this amount. Displays the error "Minimum cart is $x".'
            }
          >
            <QuestionCircleOutlined />
          </Tooltip>
        </LabelWithTooltip>
      </label>
      <InputNumber
        id="customer-properties-minimum-paid-order"
        min={0}
        step={0.25}
        value={minimumPaidOrderCents / 100}
        onChange={(value) => onMinimumPaidOrderCentsChange(Math.round((Number(value) || 0) * 100))}
      />
    </FormRow>
    <Space direction="vertical">
      <Checkbox
        className="margin-top-2"
        checked={allowFreeOrders}
        onChange={(event) => onAllowFreeOrdersChange(event.target.checked)}
      >
        <LabelWithTooltip>
          <span>Allow free orders</span>
          <Tooltip title="If the menu has $0 items such as water, they can be ordered by themselves.">
            <QuestionCircleOutlined />
          </Tooltip>
        </LabelWithTooltip>
      </Checkbox>
      {!allowFreeOrders && (
        <Checkbox
          className="margin-top-2"
          checked={allowFreeOrdersAfterPurchase}
          onChange={(event) => onAllowFreeOrdersAfterPurchaseChange(event.target.checked)}
        >
          <LabelWithTooltip>
            <span>Allow free orders after a paid order</span>
            <Tooltip title="A guest may place up to 5 orders of $0 items, during the 4 hours after buying something. Used for extra sauce, etc.">
              <QuestionCircleOutlined />
            </Tooltip>
          </LabelWithTooltip>
        </Checkbox>
      )}
      <Button className="margin-top-2" type="primary" onClick={onSave} disabled={minimumPaidOrderCents < 100}>
        Save
      </Button>
    </Space>
  </SettingsCard>
);

BasicInformationCard.propTypes = {
  timezone: PropTypes.string.isRequired,
  tzChoices: PropTypes.arrayOf(PropTypes.string).isRequired,
  currency: PropTypes.string.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string.isRequired })).isRequired,
  locale: PropTypes.string.isRequired,
  locales: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  salesforceId: PropTypes.string,
  isDashPay: PropTypes.bool.isRequired,
  ddStoreId: PropTypes.string,
  sevenroomsVenueId: PropTypes.string,
  seatedVenueId: PropTypes.string,
  referrer: PropTypes.string.isRequired,
  seatedCut: PropTypes.shape({
    seatedItemsPretax: PropTypes.number.isRequired,
    seatedItemsTax: PropTypes.number.isRequired,
    seatedServiceChargePretax: PropTypes.number.isRequired,
    seatedServiceChargeTax: PropTypes.number.isRequired,
    seatedMakeRetroactive: PropTypes.bool.isRequired,
  }),
  minimumPaidOrderCents: PropTypes.number.isRequired,
  allowFreeOrders: PropTypes.bool.isRequired,
  allowFreeOrdersAfterPurchase: PropTypes.bool.isRequired,
  onTimezoneChange: PropTypes.func.isRequired,
  onCurrencyChange: PropTypes.func.isRequired,
  onLocaleChange: PropTypes.func.isRequired,
  onSalesforceIdChange: PropTypes.func.isRequired,
  onIsDashPayChange: PropTypes.func.isRequired,
  onDdStoreIdChange: PropTypes.func.isRequired,
  onSevenroomsVenueIdChange: PropTypes.func.isRequired,
  onSeatedVenueIdChange: PropTypes.func.isRequired,
  onSeatedCutChange: PropTypes.func.isRequired,
  onSaveSeatedCut: PropTypes.func.isRequired,
  onMinimumPaidOrderCentsChange: PropTypes.func.isRequired,
  onAllowFreeOrdersChange: PropTypes.func.isRequired,
  onAllowFreeOrdersAfterPurchaseChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

BasicInformationCard.defaultProps = {
  salesforceId: "",
  ddStoreId: "",
  sevenroomsVenueId: "",
  seatedVenueId: "",
  seatedCut: null,
};

export default BasicInformationCard;
